/**
 * Карта с одной точкой.
 *
 * Leaflet подключается напрямую, без React-обёртки: нам нужна одна карта с
 * одним пином, а обёртка тянет свой жизненный цикл и требует React 19.
 * Модуль грузится лениво — карта не должна попадать в стартовый бандл.
 */

import { useEffect, useRef } from 'react'

import type { GeoPoint } from '@/shared/api/day'
import { formatTime } from '@/shared/format'
import { Sheet } from '@/shared/ui/Sheet'
import { themeColor } from '@/shared/theme'

const ACTION_LABELS: Record<string, string> = {
  shift_start: 'Начало смены',
  shift_end: 'Конец смены',
  event: 'Событие',
  task_done: 'Задача выполнена',
  here: 'Я на месте',
}

export function MapSheet({
  geo,
  timeZone,
  onClose,
}: {
  geo: GeoPoint | null
  timeZone: string
  onClose: () => void
}) {
  return (
    <Sheet open={geo !== null} onClose={onClose} title="Где это было">
      {geo && (
        <>
          <MapView geo={geo} />
          <dl className="mt-4 space-y-2 text-note">
            {geo.place_title && (
              <Line label="Место" value={`рядом: ${geo.place_title}`} />
            )}
            {geo.captured_at && (
              <Line label="Время" value={formatTime(geo.captured_at, timeZone)} />
            )}
            {geo.source_action && (
              <Line
                label="Действие"
                value={ACTION_LABELS[geo.source_action] ?? geo.source_action}
              />
            )}
            {geo.accuracy_m != null && <Line label="Точность" value={`±${geo.accuracy_m} м`} />}
          </dl>
          <p className="text-text-muted mt-4 text-caption leading-snug">
            Точка снята в момент нажатия кнопки няней. Фонового отслеживания нет.
          </p>
        </>
      )}
    </Sheet>
  )
}

function Line({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-text-muted">{label}</dt>
      <dd className="text-right font-medium">{value}</dd>
    </div>
  )
}

function MapView({ geo }: { geo: GeoPoint }) {
  const container = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let disposed = false
    let map: import('leaflet').Map | undefined

    void (async () => {
      const L = await import('leaflet')
      await import('leaflet/dist/leaflet.css')
      if (disposed || !container.current) return

      const lat = Number(geo.lat)
      const lon = Number(geo.lon)
      map = L.map(container.current, {
        center: [lat, lon],
        zoom: 16,
        zoomControl: false,
        attributionControl: true,
      })

      L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '© OpenStreetMap',
      }).addTo(map)

      // свой маркер вместо дефолтного: иконки Leaflet тянутся отдельными
      // файлами и в сборке Vite ломаются без ручной настройки путей
      const accent = themeColor('--color-accent')

      L.circleMarker([lat, lon], {
        radius: 9,
        color: accent,
        fillColor: accent,
        fillOpacity: 0.9,
        weight: 3,
      }).addTo(map)

      if (geo.accuracy_m) {
        L.circle([lat, lon], {
          radius: geo.accuracy_m,
          color: accent,
          fillColor: accent,
          fillOpacity: 0.08,
          weight: 1,
        }).addTo(map)
      }
    })()

    return () => {
      disposed = true
      map?.remove()
    }
  }, [geo])

  return (
    <div
      ref={container}
      className="bg-surface-muted h-56 w-full overflow-hidden rounded-[var(--radius-tile)]"
    />
  )
}
